export const SUPERNOVA_TOOL_CATEGORIES = Object.freeze([
  { id: 'all', label: 'All tools', icon: 'grid' },
  { id: 'agents', label: 'AI agents', icon: 'bot' },
  { id: 'research', label: 'Research', icon: 'search' },
  { id: 'experimentation', label: 'Experimentation', icon: 'flask' },
  { id: 'public-service', label: 'Public service', icon: 'building' },
  { id: 'impact', label: 'Impact', icon: 'target' },
]);

const DEFAULT_IFRAME_SAMPLE =
  'https://codepen.io/isladjan/full/vEBdwOP';
const DEFAULT_IFRAME_SANDBOX =
  'allow-downloads allow-forms allow-modals allow-pointer-lock allow-popups allow-presentation allow-same-origin allow-scripts allow-top-navigation-by-user-activation';

const createEmbeddedShowcase = ({
  title,
  about,
  src = DEFAULT_IFRAME_SAMPLE,
  height = 820,
  sandbox = DEFAULT_IFRAME_SANDBOX,
}) => ({
  about,
  embed: {
    src,
    title: `${title} interactive experience`,
    height,
    sandbox,
    allow: 'fullscreen',
  },
  actions: [
    {
      id: 'open-full-experience',
      kind: 'external',
      label: 'Open full experience',
      href: src,
      icon: 'external',
    },
  ],
});

export const SUPERNOVA_TOOLS = Object.freeze([
  {
    id: 'experiment-designer',
    slug: 'experiment-designer',
    title: 'Experiment Designer',
    description: 'Turn a challenge into a testable experiment plan.',
    longDescription:
      'Turn a challenge into a focused experiment with a clear hypothesis, success signal and next step.',
    type: 'Guided tool',
    availability: 'available',
    availabilityLabel: 'Available now',
    actionLabel: 'Open tool',
    categories: ['experimentation'],
    accent: 'teal',
    icon: 'flask',
    featured: true,
    experience: 'builder',
    builderKind: 'experiment',
    showcase: createEmbeddedShowcase({
      slug: 'experiment-designer',
      title: 'Experiment Designer',
      about:
        'Experiment Designer helps teams translate an early challenge into a focused hypothesis, an observable success signal and a practical first test.',
    }),
  },
  {
    id: 'policy-brief-copilot',
    slug: 'policy-brief-copilot',
    title: 'Policy Brief Copilot',
    description: 'Shape a clear, evidence-ready policy brief from an early idea.',
    longDescription:
      'Organise a policy question into a concise brief structure, with assumptions and evidence gaps made visible.',
    type: 'AI agent',
    availability: 'available',
    availabilityLabel: 'Available now',
    actionLabel: 'Open tool',
    categories: ['agents', 'research', 'public-service'],
    accent: 'blue',
    icon: 'document',
    featured: false,
    experience: 'chat',
    welcomeMessage:
      'Tell me the policy question you are working through. I’ll help organise it into a useful brief structure.',
    promptSuggestions: [
      'Structure a policy question',
      'Identify evidence gaps',
      'Clarify the intended outcome',
    ],
    prototypeResponse:
      'A strong first brief would separate the current challenge, the people affected, the decision required, the available evidence and the most important evidence gap. Start by naming the decision this brief needs to support.',
    showcase: createEmbeddedShowcase({
      slug: 'policy-brief-copilot',
      title: 'Policy Brief Copilot',
      about:
        'Policy Brief Copilot helps teams organise an early policy question into a clearer decision brief, exposing assumptions and evidence gaps before a document is developed.',
    }),
  },
  {
    id: 'research-navigator',
    slug: 'research-navigator',
    title: 'Research Navigator',
    description: 'Ask better questions, organise sources and build understanding.',
    longDescription:
      'Turn a broad research topic into focused questions, source priorities and a practical investigation path.',
    type: 'Chatbot',
    availability: 'available',
    availabilityLabel: 'Available now',
    actionLabel: 'Open tool',
    categories: ['research'],
    accent: 'teal',
    icon: 'chat',
    featured: false,
    experience: 'chat',
    welcomeMessage:
      'What are you trying to understand? I’ll help narrow the question and organise the next research steps.',
    promptSuggestions: [
      'Narrow a research topic',
      'Plan a source review',
      'Turn a question into sub-questions',
    ],
    prototypeResponse:
      'Begin by defining the decision or action this research should inform. Then separate what is already known, what is assumed and what must be verified. That gives you a focused set of research questions instead of an open-ended topic.',
    showcase: createEmbeddedShowcase({
      slug: 'research-navigator',
      title: 'Research Navigator',
      about:
        'Research Navigator helps users narrow broad topics into focused questions, source priorities and a practical route for building understanding.',
    }),
  },
  {
    id: 'multi-capital-impact-mapper',
    slug: 'multi-capital-impact-mapper',
    title: 'Multi-Capital Impact Mapper',
    description: 'Map intended outcomes across multiple forms of value.',
    longDescription:
      'Explore how an intervention could affect social, human, natural, financial, built and intellectual value.',
    type: 'Guided tool',
    availability: 'preview',
    availabilityLabel: 'Preview',
    actionLabel: 'Try preview',
    categories: ['impact', 'experimentation'],
    accent: 'purple',
    icon: 'chart',
    featured: false,
    experience: 'builder',
    builderKind: 'impact',
    showcase: createEmbeddedShowcase({
      slug: 'multi-capital-impact-mapper',
      title: 'Multi-Capital Impact Mapper',
      about:
        'Multi-Capital Impact Mapper provides a structured starting point for exploring how an intervention may create or reduce value across several forms of capital.',
    }),
  },
  {
    id: 'stakeholder-signal-scanner',
    slug: 'stakeholder-signal-scanner',
    title: 'Stakeholder Signal Scanner',
    description: 'Organise stakeholder signals, tensions and emerging themes.',
    longDescription:
      'Turn unstructured stakeholder observations into a clearer set of signals, tensions and questions to investigate.',
    type: 'AI agent',
    availability: 'preview',
    availabilityLabel: 'Preview',
    actionLabel: 'Try preview',
    categories: ['agents', 'research'],
    accent: 'orange',
    icon: 'signal',
    featured: false,
    experience: 'chat',
    welcomeMessage:
      'Share a stakeholder observation or recurring concern. I’ll help organise the signal without treating it as verified.',
    promptSuggestions: [
      'Organise interview notes',
      'Separate signals from assumptions',
      'Frame follow-up questions',
    ],
    prototypeResponse:
      'Treat this as an early signal rather than a conclusion. Record who raised it, the context in which it appeared, what may contradict it and the next question that would help confirm or challenge the pattern.',
    showcase: createEmbeddedShowcase({
      slug: 'stakeholder-signal-scanner',
      title: 'Stakeholder Signal Scanner',
      about:
        'Stakeholder Signal Scanner helps teams organise observations and recurring concerns into clearer signals, tensions and follow-up questions without presenting them as verified conclusions.',
    }),
  },
  {
    id: 'public-service-journey-mapper',
    slug: 'public-service-journey-mapper',
    title: 'Public Service Journey Mapper',
    description: 'Map service journeys to reveal friction and improvement points.',
    longDescription:
      'Build a shared view of a public-service journey, including moments of uncertainty, delay and opportunity.',
    type: 'Canvas',
    availability: 'coming-soon',
    availabilityLabel: 'Coming soon',
    actionLabel: 'Coming soon',
    categories: ['public-service'],
    accent: 'neutral',
    icon: 'route',
    featured: false,
    experience: 'unavailable',
    showcase: {
      about:
        'Public Service Journey Mapper will provide a shared space for mapping service journeys, moments of uncertainty, delays and opportunities for improvement.',
      embed: null,
      actions: [],
    },
  },
]);

export const findSupernovaToolBySlug = (slug) =>
  SUPERNOVA_TOOLS.find((tool) => tool.slug === slug) || null;
